import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { Typography } from '@healingpaper/cell'
import { useRouter } from 'next/router'
import { createContext, useContext, useEffect } from 'react'
import Cookies from 'js-cookie'
import { getEnv } from '@unni/platform'

import { TreatmentFilterResponseController, useTreatmentFilterReader } from '../api'

import { postAmplitude } from '@/core/lib'

import type {
  TreatmentCategory,
  TreatmentGroup,
  Treatment,
} from '@/features/treatment-category-filter/types/treatment-filter.type'
import type { Locales } from '@/utils/language'

// Context 생성
const ServiceOfferCategoryContext = createContext<{
  treatmentCategories: TreatmentCategory[]

  selectedTreatmentCategory: TreatmentCategory | undefined
  selectedTreatmentGroup: TreatmentGroup | undefined
  selectedTreatment: Treatment | undefined
}>({
  treatmentCategories: [],

  selectedTreatmentCategory: undefined,
  selectedTreatmentGroup: undefined,
  selectedTreatment: undefined,
})

const ETC_CATEGORY_ID = 423
// 메인 컴포넌트
export function ServiceOfferCategory({
  children,
  defaultTreatmentCategories,
}: {
  children: React.ReactNode
  defaultTreatmentCategories: TreatmentCategory[]
}) {
  const { q } = useRouter().query
  const { i18n } = useTranslation()
  const header = {
    Authorization: Cookies.get('token') || '',
    'Accept-Language': i18n.language as Locales,
  }

  const { data } = useTreatmentFilterReader(header)
  const treatmentCategories = data?.treatmentCategories || defaultTreatmentCategories || []

  const treatmentCategoryController = new TreatmentFilterResponseController({ treatmentCategories })
  const categoryId = useRouter().query.categoryId || treatmentCategoryController.getCategoryUnitByName(q as string)?.id

  const {
    treatmentCategories: selectedTreatmentCategory,
    treatmentGroups: selectedTreatmentGroup,
    treatments: selectedTreatment,
  } = treatmentCategoryController.getCategoryCombinationByNameAndId(q as string, Number(categoryId))

  return (
    <ServiceOfferCategoryContext.Provider
      value={{
        treatmentCategories: treatmentCategories.sort((prevCategory, nextCategory) => {
          if (prevCategory.id === ETC_CATEGORY_ID) {
            return 1
          }
          if (nextCategory.id === ETC_CATEGORY_ID) {
            return -1
          }
          return 0
        }),

        selectedTreatmentCategory,
        selectedTreatmentGroup,
        selectedTreatment,
      }}
    >
      {children}
    </ServiceOfferCategoryContext.Provider>
  )
}

interface Props {
  className?: string
}

const TOTAL_TREATMENT_CATEGORY_ID = 10000

const TreatmentCategoryList = ({ className }: Props): JSX.Element => {
  const { treatmentCategories, selectedTreatmentCategory } = useContext(ServiceOfferCategoryContext)
  const { t } = useTranslation()
  const router = useRouter()
  const displayTreatmentCategories = [
    {
      id: TOTAL_TREATMENT_CATEGORY_ID,
      name: t('components.eventHeader.contents.all'),
    },
    ...treatmentCategories,
  ]

  const categoryId = selectedTreatmentCategory?.id
  const categoryName = selectedTreatmentCategory?.name

  useEffect(() => {
    if (!selectedTreatmentCategory) {
      return
    }
    const elementId = `${selectedTreatmentCategory.id}_${selectedTreatmentCategory.name}`
    if (elementId) {
      const selectedTreatmentElement = document.getElementById(elementId)
      if (selectedTreatmentElement) {
        selectedTreatmentElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  }, [selectedTreatmentCategory])

  return (
    <nav className={`flex overflow-x-auto border-b border-[#E5E5E5] hide-scrollbar ${className || ''}`}>
      <ul className="flex flex-row px-3">
        {displayTreatmentCategories.map(({ name, id }) => {
          const isTotal = id === TOTAL_TREATMENT_CATEGORY_ID
          const params = new URLSearchParams()
          if (!isTotal) {
            params.set('q', name)
            params.set('categoryId', id.toString())
          }
          if (router.query.district) {
            params.set('district', router.query.district as string)
          }

          const queryString = params.toString()
          const href = `/events${queryString ? `?${queryString}` : ''}`

          const isSelected = (categoryId === id && categoryName === name) || (isTotal && categoryId === undefined)
          return (
            <Link
              key={name}
              id={`${id}_${name}`}
              className={`px-2 h-14 flex items-center border-b-[3px] ${isSelected ? ' border-[#FF6600]' : 'border-opacity-0'}`}
              href={href}
              onClick={() => {
                postAmplitude('click_category', {
                  type: 'treatment_category',
                  category_id: id,
                  category_name: name,
                  unique_category_identifier: `${id}_${name}`,
                })
              }}
            >
              <Typography
                type="title3"
                style={{
                  color: isSelected ? 'black' : '#999999',
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </Typography>
            </Link>
          )
        })}
      </ul>
    </nav>
  )
}

const TREATMENT_GROUP_CATEGORY_ID = 20000
const TreatmentGroupList = () => {
  const { i18n, t } = useTranslation()
  const router = useRouter()
  const prefix = i18n.language.includes('ko') ? '' : '/jp'
  const { selectedTreatmentCategory, selectedTreatmentGroup } = useContext(ServiceOfferCategoryContext)
  const treatmentGroups = selectedTreatmentCategory?.treatmentGroups || []
  const displayTreatmentGroups = [
    {
      id: TREATMENT_GROUP_CATEGORY_ID,
      name: t('components.eventHeader.contents.all'),
      count: 0,
    },
    ...treatmentGroups,
  ]

  useEffect(() => {
    if (!selectedTreatmentGroup) {
      return
    }
    const elementId = `${selectedTreatmentGroup?.id}_${selectedTreatmentGroup?.name}`

    if (elementId) {
      const selectedTreatmentGroupElement = document.getElementById(elementId)
      if (selectedTreatmentGroupElement) {
        selectedTreatmentGroupElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  }, [selectedTreatmentGroup])

  if (treatmentGroups?.length === 0 || !treatmentGroups) {
    return <></>
  }

  return (
    <div className="flex overflow-x-auto px-5 py-4 hide-scrollbar">
      <ul className="flex flex-row gap-4">
        {displayTreatmentGroups?.map(({ name, id }) => {
          const isTotal = id === TREATMENT_GROUP_CATEGORY_ID
          const params = new URLSearchParams()
          if (isTotal) {
            const { id: treatmentCategoryId, name: treatmentCategoryName } = selectedTreatmentCategory || {}
            params.set('q', treatmentCategoryName || '')
            params.set('categoryId', treatmentCategoryId?.toString() || '')
          } else {
            params.set('q', name || '')
            params.set('categoryId', id?.toString() || '')
          }
          if (router.query.district) {
            params.set('district', router.query.district as string)
          }

          const queryString = params.toString()
          const href = `/events${queryString ? `?${queryString}` : ''}`

          const isSelected =
            (selectedTreatmentGroup?.id === id && selectedTreatmentGroup?.name === name) ||
            (isTotal && selectedTreatmentGroup?.id === undefined)
          return (
            <Link
              key={name}
              id={`${id}_${name}`}
              className="flex items-center"
              href={href}
              onClick={() => {
                postAmplitude('click_category', {
                  type: 'treatment_group',
                  category_id: id,
                  category_name: name,
                  unique_category_identifier: `${id}_${name}`,
                })
              }}
            >
              <Typography
                type="subTitle2"
                color={isSelected ? 'black' : 'gray700'}
                style={{
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </Typography>
            </Link>
          )
        })}
      </ul>
    </div>
  )
}

const TOTAL_TREATMENT_ID = 30000
const TreatmentList = () => {
  const { i18n, t } = useTranslation()
  const router = useRouter()
  const prefix = i18n.language.includes('ko') ? '' : '/jp'
  const { selectedTreatmentGroup, selectedTreatment } = useContext(ServiceOfferCategoryContext)

  const treatments = selectedTreatmentGroup?.treatments || []

  const displayTreatments = [
    {
      id: TOTAL_TREATMENT_ID,
      name: t('components.eventHeader.contents.all'),
      count: 0,
    },
    ...treatments,
  ]

  useEffect(() => {
    if (!selectedTreatment) {
      return
    }
    const elementId = `${selectedTreatment?.id}_${selectedTreatment?.name}`
    if (elementId) {
      const selectedTreatmentElement = document.getElementById(elementId)
      if (selectedTreatmentElement) {
        selectedTreatmentElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  }, [selectedTreatment])

  if (treatments?.length === 0 || !treatments) {
    return <></>
  }

  return (
    <div className="flex overflow-x-auto bg-[#FAFAFA] px-5 py-4 hide-scrollbar">
      <ul className="flex flex-row gap-4">
        {displayTreatments?.map(({ name, id }) => {
          const isTotal = id === TOTAL_TREATMENT_ID
          const params = new URLSearchParams()
          if (isTotal) {
            const { id: treatmentGroupId, name: treatmentGroupName } = selectedTreatmentGroup || {}
            params.set('q', treatmentGroupName || '')
            params.set('categoryId', treatmentGroupId?.toString() || '')
          } else {
            params.set('q', name || '')
            params.set('categoryId', id?.toString() || '')
          }
          if (router.query.district) {
            params.set('district', router.query.district as string)
          }

          const queryString = params.toString()
          const href = `/events${queryString ? `?${queryString}` : ''}`

          const isSelected =
            (selectedTreatment?.id === id && selectedTreatment?.name === name) ||
            (isTotal && selectedTreatment?.id === undefined)
          return (
            <Link
              key={id}
              id={`${id}_${name}`}
              className="flex items-center"
              href={href}
              onClick={() => {
                postAmplitude('click_category', {
                  type: 'treatment',
                  category_id: id,
                  category_name: name,
                  unique_category_identifier: `${id}_${name}`,
                })
              }}
            >
              <Typography
                type="subTitle2"
                color={isSelected ? 'black' : 'gray700'}
                style={{
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </Typography>
            </Link>
          )
        })}
      </ul>
    </div>
  )
}

ServiceOfferCategory.TreatmentCategoryList = TreatmentCategoryList
ServiceOfferCategory.TreatmentGroupList = TreatmentGroupList
ServiceOfferCategory.TreatmentList = TreatmentList
